import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';

function Settings() {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    site_name: '',
    site_email: '',
    site_phone: '',
    site_address: '',
    currency: 'GHS',
    tax_rate: '',
    shipping_fee: '',
    low_stock_threshold: 10,
    enable_notifications: true,
    enable_email_notifications: true,
    site_active: true, // Site status: true = active, false = maintenance mode
  });

  useEffect(() => {
    // Load settings from localStorage or API
    const loadSettings = async () => {
      try {
        const savedSettings = localStorage.getItem('admin_settings');
        if (savedSettings) {
          setSettings(JSON.parse(savedSettings));
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Save to localStorage (in a real app, this would be an API call)
      localStorage.setItem('admin_settings', JSON.stringify(settings));
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      showSuccess('Settings saved successfully!');
      
      // Trigger a custom event to notify App.jsx about site status change
      window.dispatchEvent(new CustomEvent('siteStatusChanged', { 
        detail: { site_active: settings.site_active } 
      }));
    } catch (error) {
      showError('Failed to save settings. Please try again.');
      console.error('Failed to save settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      const defaultSettings = {
        site_name: '',
        site_email: '',
        site_phone: '',
        site_address: '',
        currency: 'GHS',
        tax_rate: '',
        shipping_fee: '',
        low_stock_threshold: 10,
        enable_notifications: true,
        enable_email_notifications: true,
        site_active: true,
      };
      setSettings(defaultSettings);
      localStorage.removeItem('admin_settings');
      showSuccess('Settings reset to default values.');
    }
  };

  return (
    <div>
      {/* Header Section */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-1.5 h-8 md:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight drop-shadow-sm">Settings</h1>
            <p className="text-xs md:text-sm text-gray-600 font-medium mt-1">Manage your application settings</p>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-xl md:rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
        <form onSubmit={handleSubmit} className="p-4 md:p-6 lg:p-8">
          <div className="space-y-6 md:space-y-8">
            {/* Site Status */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 pb-2 md:pb-3 border-b-2 border-gray-200">
                Site Status
              </h2>
              <div className="bg-gradient-to-r from-primary/5 via-primary-dark/5 to-secondary/5 rounded-xl md:rounded-2xl border-2 border-primary/20 p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                      Site Availability
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-4">
                      {settings.site_active 
                        ? 'Your site is currently active and accessible to all visitors.'
                        : 'Your site is currently in maintenance mode. Only administrators can access the admin panel.'}
                    </p>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm font-bold ${
                        settings.site_active
                          ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                          : 'bg-orange-100 text-orange-700 border-2 border-orange-300'
                      }`}>
                        {settings.site_active ? '🟢 Active' : '🟠 Maintenance Mode'}
                      </span>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      name="site_active"
                      checked={settings.site_active}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="relative w-14 md:w-16 h-7 md:h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 md:after:h-7 after:w-6 md:after:w-7 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:via-emerald-600 peer-checked:to-emerald-700 shadow-lg peer-checked:shadow-emerald-500/50 transition-all duration-300"></div>
                    <span className="ml-3 text-sm md:text-base font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">
                      {settings.site_active ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* General Settings */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 pb-2 md:pb-3 border-b-2 border-gray-200">
                General Settings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Site Name
                  </label>
                  <input
                    type="text"
                    name="site_name"
                    value={settings.site_name}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                    placeholder="Enter site name"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Site Email
                  </label>
                  <input
                    type="email"
                    name="site_email"
                    value={settings.site_email}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                    placeholder="admin@example.com"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Site Phone
                  </label>
                  <input
                    type="tel"
                    name="site_phone"
                    value={settings.site_phone}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                    placeholder="+233 XX XXX XXXX"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={settings.currency}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium bg-white"
                  >
                    <option value="GHS">GHS - Ghana Cedis</option>
                    <option value="USD">USD - US Dollars</option>
                    <option value="EUR">EUR - Euros</option>
                    <option value="GBP">GBP - British Pounds</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Site Address
                  </label>
                  <textarea
                    name="site_address"
                    value={settings.site_address}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium resize-none"
                    placeholder="Enter site address"
                  />
                </div>
              </div>
            </div>

            {/* Business Settings */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 pb-2 md:pb-3 border-b-2 border-gray-200">
                Business Settings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    name="tax_rate"
                    value={settings.tax_rate}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    max="100"
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Default Shipping Fee ({settings.currency})
                  </label>
                  <input
                    type="number"
                    name="shipping_fee"
                    value={settings.shipping_fee}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                    Low Stock Threshold
                  </label>
                  <input
                    type="number"
                    name="low_stock_threshold"
                    value={settings.low_stock_threshold}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                    placeholder="10"
                  />
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1">
                    Products with stock below this number will be marked as low stock
                  </p>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 pb-2 md:pb-3 border-b-2 border-gray-200">
                Notification Settings
              </h2>
              <div className="space-y-3 md:space-y-4">
                <label className="flex items-center gap-2 md:gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="enable_notifications"
                    checked={settings.enable_notifications}
                    onChange={handleChange}
                    className="w-4 h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 cursor-pointer"
                  />
                  <span className="text-xs md:text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Enable Browser Notifications
                  </span>
                </label>

                <label className="flex items-center gap-2 md:gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="enable_email_notifications"
                    checked={settings.enable_email_notifications}
                    onChange={handleChange}
                    className="w-4 h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 cursor-pointer"
                  />
                  <span className="text-xs md:text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Enable Email Notifications
                  </span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 md:gap-3 lg:gap-4 pt-4 md:pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="w-full sm:w-auto px-3 md:px-4 lg:px-6 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-300 text-gray-700 text-xs md:text-sm lg:text-base font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset to Default
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-3 md:px-4 lg:px-6 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl bg-gradient-to-r from-primary via-primary-dark to-secondary text-white text-xs md:text-sm lg:text-base font-semibold hover:shadow-xl hover:shadow-primary/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Settings;

